import { extendArrayWithContains } from "./arrayContains.js";
import { clinicHelpers, parseDate, parseTime } from "./viedoc-clinic-common.js";
import { isDateTimeItem } from "./form-helpers.js";
import { getTelemetry, SEVERITY } from "./telemetry.js";
import { reportDesignFault, reportDesignFaultResolved } from "./design-fault-reporting.js";

// Patch Array.prototype.contains once at module load — the legacy JINT
// sandbox on the server-side has this helper available globally, so
// expressions written against ViedocClinicCommon may rely on it.
extendArrayWithContains();

// ---------------------------------------------------------------------------
// Compiled-sandbox cache
// ---------------------------------------------------------------------------

/**
 * Cache key: `expression + "|" + paramNames.join(",")`, where paramNames are in
 * BINDING ORDER (not sorted).
 *
 * Keying on both the expression string and the param-name list — in the order
 * the compiled Function binds them — guarantees that two callers asking for the
 * same expression text but with a different local shape (different arity, or the
 * same names in a different order) get distinct compiled Functions. Sorting the
 * names would let two callers with the same name-set but a different binding
 * order collide on one compiled Function and bind arguments to the wrong
 * parameters (see the note at the cache-key computation in createSandboxFn).
 * Within a single form the param list for a given expression is stable after the
 * initial compile, so subsequent keystrokes hit the cache.
 *
 * Bounded by `clearSandboxCache()` which is called from the form reducer when
 * a new form definition is loaded, so the cache never grows beyond a single
 * form's expression set.
 */
const _sandboxCache = new Map();

/**
 * Drop all compiled sandbox entries.  Called when a new form definition is
 * loaded so expressions from the previous form don't linger in memory.
 */
export function clearSandboxCache() {
    _sandboxCache.clear();
}

// ---------------------------------------------------------------------------
// Sandbox internals (extracted from form-visibility.js)
// ---------------------------------------------------------------------------

function clone(obj) {
    if (obj === null || typeof obj !== "object" || "isActiveClone" in obj)
        return obj;

    let clonedObject;
    if (obj instanceof Date) {
        clonedObject = new Date(obj);
    } else if (obj.constructor === Array) {
        clonedObject = obj.slice(0);
    } else {
        clonedObject = obj.constructor();
        for (const propName in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, propName)) {
                obj.isActiveClone = null;
                clonedObject[propName] = clone(obj[propName]);
                // biome-ignore lint/performance/noDelete: needed to break cyclic-clone guard
                delete obj.isActiveClone;
            }
        }
    }
    return clonedObject;
}

function createSandboxFn(code, locals) {
    const params = [];
    const args = [];
    for (const propName in locals) {
        if (Object.prototype.hasOwnProperty.call(locals, propName)) {
            args.push(locals[propName]);
            params.push(propName);
        }
    }

    // Cache key: expression code + param names IN BINDING ORDER. The compiled
    // Function binds `args` positionally to `params` (line below), and cache
    // hits re-bind the current call's args to the *previously compiled* param
    // list — so the key must reflect the actual param order, not a sorted one.
    // Sorting here let two callers with the same name-set but different
    // insertion order (e.g. a visibility pass vs a range-check pass resolving
    // the same OIDs from different source objects) collide on one compiled fn
    // and bind args to the wrong parameters, yielding a wrong computed value.
    const cacheKey = `${code}|${params.join(",")}`;
    let sandbox = _sandboxCache.get(cacheKey);
    if (!sandbox) {
        const that = Object.create(null);
        const context = Array.prototype.concat.call(that, params, code);
        sandbox = new (Function.prototype.bind.apply(Function, context))();
        _sandboxCache.set(cacheKey, sandbox);
    }

    const that = Object.create(null);
    const argsContext = Array.prototype.concat.call(that, args);
    return Function.prototype.bind.apply(sandbox, argsContext);
}

function getIndexesOfKeyWord(text, keyword, index, indexes) {
    // biome-ignore lint/style/noParameterAssign: recursive accumulator
    indexes = indexes || [];
    const indexOfKeyWord = text.indexOf(keyword, index);
    if (indexOfKeyWord > -1) {
        const lastIndexOfKeyWord = indexOfKeyWord + keyword.length - 1;
        indexes.push({ first: indexOfKeyWord, last: lastIndexOfKeyWord });
        getIndexesOfKeyWord(text, keyword, lastIndexOfKeyWord + 1, indexes);
    }
    return indexes;
}

function containsKeyWord(text, keyword) {
    const indexOfKeyWord = getIndexesOfKeyWord(text, keyword, 0);
    for (let i = 0; i < indexOfKeyWord.length; i++) {
        const charBeforeKeyWord = text[indexOfKeyWord[i].first - 1];
        const charAfterKeyWord = text[indexOfKeyWord[i].last + 1];
        if (
            (charBeforeKeyWord == null || charBeforeKeyWord.match(/\W/)) &&
            (charAfterKeyWord == null || charAfterKeyWord.match(/\W/))
        ) {
            return true;
        }
    }
    return false;
}

function extendLocals(toObj, fromObj, searchText) {
    for (const propName in fromObj) {
        if (Object.prototype.hasOwnProperty.call(fromObj, propName)) {
            if (containsKeyWord(searchText, propName)) {
                toObj[propName] = clone(fromObj[propName]);
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Matches an ISO date / datetime string ("2020-01-01", "2020-01-01T00:00",
// "…T00:00:00.000Z", "… +02:00"). Deliberately strict so identifier codes and
// other non-date strings ("001", "2020") never match.
// Fractional seconds are only valid after a seconds component, so ".5" is kept
// inside the optional seconds group rather than allowed on its own ("10:30.5").
const ISO_DATE_RE =
    /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Parses a date string leniently via parseDate, returning null when it does
 * not produce a valid Date. Never throws — a malformed stored value must
 * degrade to the original string, not crash the reducer or effect that
 * builds the expression context.
 */
function tryParseDate(str) {
    try {
        const d = parseDate(str);
        return d && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
        return null;
    }
}

/**
 * Strict variant for values with no declared item type (dependencies,
 * identifierModel.EventDate): only strings shaped like an ISO date convert.
 */
const parseIsoDateStrict = (value) =>
    typeof value === "string" && ISO_DATE_RE.test(value) ? tryParseDate(value) : null;

/**
 * Coerces a single formData value to the JS type implied by its item type.
 * formData holds every value as a string (NumberInput/DateTimeItem emit
 * strings), so bare relational operators (`>`, `<=`, …) would otherwise
 * compare two strings lexicographically or coerce a string-vs-Date to NaN.
 * Untyped keys (no itemType — e.g. `__format` sidecar keys or merged
 * dependency values) and null/array values are left untouched, as is any
 * value that does not parse (keeping the original avoids masking a bad value as
 * NaN/Invalid Date). A blank string becomes null: see below.
 */
function coerceValueByType(value, itemType) {
    if (!itemType || value == null || Array.isArray(value)) return value;

    // A cleared input emits "" (a text item can also hold only whitespace).
    // Clinic stores either as null (ItemData.SetValue → IsNull) and the server
    // evaluates range checks against null (PROFormValidationService), so the
    // expression must see null here too — otherwise a designer's `X == null`
    // guard on a referenced item is false after the participant clears it, and
    // the client blocks a submission the server would accept (WI 104787). This
    // also keeps Number(" ") from ever reading as 0.
    if (typeof value === "string" && value.trim() === "") return null;

    // Nrs and VasScale are numeric like Number: NRS values round-trip through
    // the <viedoc-radio> DOM attribute (event.target.value) and so arrive as
    // strings even when choiceValueTyped is a number. The isFinite guard below
    // keeps any non-numeric choice code as its original string.
    if (itemType === "Number" || itemType === "Nrs" || itemType === "VasScale") {
        const n = Number(value);
        // isFinite rejects NaN and ±Infinity, so "Infinity" keeps its original
        // string instead of comparing as a numeric infinity.
        return Number.isFinite(n) ? n : value;
    }

    if (isDateTimeItem(itemType)) {
        if (value instanceof Date) return value;
        if (typeof value !== "string") return value;
        // A time-only value has no date part; parseDate would misread the
        // hour as a year, so anchor it to today via parseTime instead. The
        // matched digits guarantee parseTime cannot throw here.
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) return parseTime(value);
        return tryParseDate(value) ?? value;
    }

    // Any other item type is either a typed non-string or a coded string
    // that compares correctly as-is.
    return value;
}

function coerceFormDataByType(formData, itemTypes) {
    const coerced = {};
    // Seed every declared item OID with null first. The live API always sends
    // every OID as a formData key (null until answered), but a preview
    // questModel ships no formData keys at all — and an OID that is absent
    // from the locals never becomes a sandbox parameter, so a bare `BMWGHT`
    // in an expression resolves to the global DOM element with that id
    // instead of null, falsely violating range checks. Seeding restores the
    // live-mode contract; when the key is already present this is a no-op.
    for (const oid of Object.keys(itemTypes)) {
        coerced[oid] = null;
    }
    if (formData == null) return coerced;
    for (const key of Object.keys(formData)) {
        coerced[key] = coerceValueByType(formData[key], itemTypes[key]);
    }
    return coerced;
}

/**
 * Converts Date-typed values inside `dependencies` to JS Dates, matching the
 * server (JINT), which runs `date(...)` over every Date dependency before
 * evaluating (ConditionEvaluator.RunFormDataJson / ConvertDependenciesDate...).
 * The Me API sends those values as ISO strings, so a cross-form date dependency
 * would otherwise compare as a string (or coerce to NaN against date()/today()).
 *
 * The dependency payload carries no item-type metadata, so this is a shape
 * heuristic: any strict ISO date string is converted, unlike the server which
 * converts only Date-typed values. ISO_DATE_RE is deliberately strict so coded
 * strings ("001", "2020") never match; numbers already arrive typed. The
 * original object is returned unchanged when it holds no dates, so the common
 * case keeps reference identity.
 */
function coerceDependencyDates(deps) {
    if (deps == null || typeof deps !== "object") return deps;

    const walk = (value) => {
        if (typeof value === "string") {
            return parseIsoDateStrict(value) ?? value;
        }
        // Recurse into grouped dependency objects (deps nest by form/event);
        // leave arrays and existing Date instances untouched.
        if (
            value == null ||
            typeof value !== "object" ||
            Array.isArray(value) ||
            value instanceof Date
        ) {
            return value;
        }
        // Copy-on-write: clone an object only once a child actually changes, so
        // objects holding no dates keep their reference and unchanged sibling
        // branches are shared rather than deep-copied.
        let output = value;
        for (const key of Object.keys(value)) {
            const original = value[key];
            const converted = walk(original);
            if (converted !== original) {
                if (output === value) output = { ...value };
                output[key] = converted;
            }
        }
        return output;
    };

    return walk(deps);
}

/**
 * Converts identifierModel.EventDate to a JS Date, matching the server, which
 * runs `EventDate = date(EventDate)` before evaluating (ConditionEvaluator.
 * RunFormDataJson). Only that key is touched — the rest of identifierModel
 * (site codes, ids) must keep its original types.
 */
function coerceIdentifierEventDate(identifierModel) {
    const d = parseIsoDateStrict(identifierModel?.EventDate);
    return d ? { ...identifierModel, EventDate: d } : identifierModel;
}

// dependencies and identifierModel are fetched once per form session and never
// mutated afterwards, so their converted form is cached by input identity —
// contexts are otherwise rebuilt on every keystroke and would repeat the same
// tree walk each time. WeakMap entries die with the source objects, so the
// caches need no clearing when a new form loads.
const coercedDepsCache = new WeakMap();
const coercedIdentifierCache = new WeakMap();

function memoizeByIdentity(cache, value, convert) {
    if (value == null || typeof value !== "object") return convert(value);

    let converted = cache.get(value);
    if (converted === undefined) {
        converted = convert(value);
        cache.set(value, converted);
    }
    return converted;
}

/**
 * Builds a locals object from the three context sources that expressions can
 * reference.  Pass the result to `evaluateExpression` or `evaluateExpressionSafe`.
 *
 * When `itemTypes` (an OID→itemType map) is supplied, formData values are
 * coerced to their declared type first so range-check comparisons behave like
 * the server-side (JINT) evaluation. Omit it to pass the values through
 * unchanged.
 *
 * @param {{
 *   formData: object,
 *   dependencies: object,
 *   identifierModel: object,
 *   itemTypes?: Record<string, string>
 * }} sources
 * @returns {{ formData: object, dependencies: object, identifierModel: object }}
 */
export function createExpressionContext({ formData, dependencies, identifierModel, itemTypes }) {
    return {
        // formData holds strings from the input components, so it is coerced by
        // the current form's itemTypes (Number/Date). identifierModel is passed
        // through unchanged — blindly number-coercing it would corrupt string
        // identifier codes (e.g. a site code "001" must stay a string for
        // `SITE === '001'` to hold).
        formData: itemTypes ? coerceFormDataByType(formData, itemTypes) : formData,
        // dependencies carry no itemType here, but the server converts Date-typed
        // dependency values to Dates before evaluating; mirror that for the ISO
        // date strings the API sends (numbers already arrive typed).
        dependencies: memoizeByIdentity(coercedDepsCache, dependencies, coerceDependencyDates),
        identifierModel: memoizeByIdentity(
            coercedIdentifierCache,
            identifierModel,
            coerceIdentifierEventDate
        ),
    };
}

/**
 * Evaluates a Viedoc expression string against a context created by
 * `createExpressionContext`.  The expression has access to all properties from
 * `formData`, `dependencies`, and `identifierModel` as bare-name locals, plus
 * all ViedocClinicCommon helpers (date, today, age, bmi, …).
 *
 * Throws on parse / compile errors; does NOT catch runtime errors from the
 * expression itself.  Use `evaluateExpressionSafe` when a fallback value is
 * preferred.
 *
 * @param {string} expression
 * @param {{ formData: object, dependencies: object, identifierModel: object }} context
 * @returns {*}
 */
export function evaluateExpression(expression, context) {
    const { formData, dependencies, identifierModel } = context;

    const locals = { ...clinicHelpers };
    extendLocals(locals, dependencies, expression);
    extendLocals(locals, identifierModel, expression);
    extendLocals(locals, formData, expression);

    // Every expression arriving here is a single JS expression — visibility
    // booleans, range-check comparisons, ternary computations, and (since
    // Viedoc.Format.Cdisc.Extensions.JsExpressionWrap.WrapAsIife) MethodDef
    // Computation bodies wrapped as IIFEs. Prefixing `return ` is enough.
    const sandbox = createSandboxFn(`return ${expression}`, locals);
    return sandbox();
}

/**
 * Like `evaluateExpression` but catches all errors and returns `defaultValue`
 * instead.  Mirrors the behaviour of the visibility sandbox in form-visibility.js.
 *
 * @param {string} expression
 * @param {{ formData: object, dependencies: object, identifierModel: object }} context
 * @param {*} [defaultValue=undefined]
 * @param {{ source: string, oid?: string, label?: string }} [origin]
 *   Which authored expression this is, for the design-fault sink. Optional
 *   because a caller with nothing useful to say about provenance is better off
 *   saying nothing than guessing — the fault is still tracked, it is just not
 *   attributable in the preview panel.
 * @returns {*}
 */
export function evaluateExpressionSafe(
    expression,
    context,
    defaultValue = undefined,
    origin = null
) {
    // Guard: null/empty expression has no meaningful result — return the default
    // immediately.  Without this, `new Function('return ' + null)` would return
    // `null` (falsy) and silently trigger false Soft/Hard violations.
    if (expression == null || expression === "") return defaultValue;

    try {
        const value = evaluateExpression(expression, context);
        // A clean run lifts any fault the preview panel lists for this
        // expression: a data-dependent throw (`W.toFixed()` while W is still
        // null) must disappear once the designer supplies the value. No-op
        // outside the preview.
        reportDesignFaultResolved(origin, expression);
        return value;
    } catch (ex) {
        // Surface designer-expression failures to telemetry so Ops can find
        // and triage them. Parse errors are Errors (definitely a designer bug);
        // anything else is a Warning (could be transient data shape).
        const isParseError = ex instanceof SyntaxError;
        getTelemetry().trackException({
            exception: ex,
            properties: {
                expression: expression.slice(0, 200),
                errorType: isParseError ? "parse" : "runtime",
            },
            severityLevel: isParseError ? SEVERITY.ERROR : SEVERITY.WARNING,
        });
        // Same fault, second audience: telemetry is for OOps after the fact,
        // the sink is for the designer looking at the form right now. No-op
        // outside the preview.
        reportDesignFault({
            fault: "expression-error",
            source: origin?.source ?? null,
            oid: origin?.oid ?? "",
            label: origin?.label ?? "",
            expression,
            message: String(ex?.message ?? ex),
        });
        return defaultValue;
    }
}
